import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Loader2, RefreshCw, ClipboardCheck } from "lucide-react";
import { formatDateTimeIST } from "@/lib/formatUtils";
import { generateShiftSummaryPDF } from "@/lib/pdfGenerator";
import logger from "@/lib/logger";

const rupee = (n) => `\u20b9${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function PendingBlock({ block }) {
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`pending-${block.key}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-amber-50 border-b">
        <span className="text-xs font-bold uppercase tracking-wider text-amber-800">{block.label}</span>
        <span className="text-sm font-black text-amber-900" style={{ fontFamily: "Chivo" }}>{block.count}</span>
      </div>
      <div className="divide-y max-h-44 overflow-y-auto">
        {block.items.length === 0 && <p className="px-3 py-3 text-xs text-gray-400">Nothing pending</p>}
        {block.items.map(item => (
          <div key={`${block.key}-${item.ref_no}-${item.primary}`} className="px-3 py-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{item.primary || "\u2014"}</p>
              <p className="text-[11px] text-gray-500 truncate">{item.secondary || "\u2014"}</p>
            </div>
            <span className="font-mono text-[10px] text-gray-400 shrink-0">{item.ref_no}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ShiftSummaryReport() {
  const { location } = useAuth();
  const [date, setDate] = useState("");
  const [shift, setShift] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = { location };
      const d = opts.date ?? date;
      const s = opts.shift ?? shift;
      if (d) params.date = d;
      if (s) params.shift = s;
      const { data } = await api.get("/handover/shift-summary", { params });
      setSummary(data);
      setDate(data.date);
      setShift(data.shift);
    } catch (err) {
      logger.error("Failed to load shift summary:", err);
      toast.error(err.response?.data?.detail || "Failed to load shift summary");
    }
    setLoading(false);
  }, [location, date, shift]);

  useEffect(() => { fetchSummary({ date: "", shift: "" }); /* initial load = current shift */ }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePdf = () => {
    if (!summary) return;
    generateShiftSummaryPDF({ summary });
  };

  return (
    <div className="space-y-4" data-testid="shift-summary-report">
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-10 w-full sm:w-44" data-testid="shift-summary-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Shift</Label>
            <Select value={shift} onValueChange={setShift}>
              <SelectTrigger className="h-10 w-full sm:w-60" data-testid="shift-summary-shift"><SelectValue placeholder="Select shift" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="A">A (08:00 AM - 08:00 PM)</SelectItem>
                <SelectItem value="B">B (08:00 PM - 08:00 AM)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => fetchSummary()} disabled={loading} className="h-10 bg-[#1B3C87] hover:bg-[#2650A8] text-xs" data-testid="shift-summary-generate">
            {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />} Generate
          </Button>
          <Button variant="outline" onClick={handlePdf} disabled={!summary} className="h-10 text-xs" data-testid="shift-summary-pdf">
            <FileText size={14} className="mr-1" /> PDF
          </Button>
        </div>
      </Card>

      {summary && (
        <>
          <Card className="p-4" data-testid="shift-summary-header">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="font-bold text-base" style={{ fontFamily: "Chivo" }}>
                  Shift {summary.shift_label} &middot; {summary.date}
                </h3>
                <p className="text-xs text-gray-500">
                  {summary.location} &middot; {formatDateTimeIST(summary.window_start)} to {formatDateTimeIST(summary.window_end)}
                  {summary.is_current_shift && <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Live</span>}
                </p>
              </div>
              <div className="flex gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Entries</p>
                  <p className="text-2xl font-black" style={{ fontFamily: "Chivo" }} data-testid="shift-total-entries">{summary.total_entries}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Purchase (With GST)</p>
                  <p className="text-2xl font-black text-emerald-700" style={{ fontFamily: "Chivo" }} data-testid="shift-purchase-value">{rupee(summary.purchase.total_with_gst)}</p>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4" data-testid="shift-module-counts">
            <h4 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-3">Entries By Module</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {summary.modules.map(m => (
                <div key={m.collection} className={`px-3 py-2 rounded border ${m.count > 0 ? "border-[#1B3C87]/20 bg-[#1B3C87]/5" : "border-gray-100"}`}>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 truncate">{m.label}</p>
                  <p className={`text-lg font-black ${m.count > 0 ? "text-[#1B3C87]" : "text-gray-300"}`} style={{ fontFamily: "Chivo" }}>{m.count}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-4" data-testid="shift-purchase-breakdown">
            <h4 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-3">Purchase Value Logged</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {summary.purchase.modules.map(m => (
                <div key={m.collection} className="px-3 py-2 rounded border border-emerald-100 bg-emerald-50/50">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 truncate">{m.label}</p>
                  <p className="text-base font-black text-emerald-900" style={{ fontFamily: "Chivo" }}>{rupee(m.with_gst)}</p>
                  <p className="text-[11px] text-emerald-700">Without GST {rupee(m.without_gst)} &middot; {m.entries} entries</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-sm font-semibold">
              Total: {rupee(summary.purchase.total_with_gst)} with GST &middot; {rupee(summary.purchase.total_without_gst)} without GST
            </p>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="shift-pending-section">
            {summary.pending.map(block => <PendingBlock key={block.key} block={block} />)}
          </div>

          <Card className="p-4" data-testid="shift-handover-signoff">
            <h4 className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-2 flex items-center gap-2">
              <ClipboardCheck size={14} /> Guard Sign-Off
            </h4>
            {summary.handover ? (
              <div className="text-sm space-y-1">
                <p><span className="text-gray-500">Outgoing:</span> <span className="font-semibold">{summary.handover.outgoing_name || summary.handover.outgoing_guard}</span> at {formatDateTimeIST(summary.handover.outgoing_sign_time)}</p>
                <p><span className="text-gray-500">Incoming:</span> <span className="font-semibold">{summary.handover.incoming_name || "\u2014"}</span> {summary.handover.incoming_acknowledged ? `acknowledged at ${formatDateTimeIST(summary.handover.incoming_sign_time)}` : "(pending acknowledgement)"}</p>
                {summary.handover.outgoing_remarks && <p><span className="text-gray-500">Remarks:</span> {summary.handover.outgoing_remarks}</p>}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No handover signed off for this shift yet.</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
